import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { authUtils } from '@/services/auth'

interface ProtectedRouteProps {
  children: ReactNode
  requireAdmin?: boolean
}

const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth()
  const location = useLocation()
  const [verificandoLocalStorage, setVerificandoLocalStorage] = useState(true)

  // Verificar localStorage como fallback (importante após login OAuth2)
  useEffect(() => {
    // Dar um pequeno delay para garantir que o contexto foi atualizado
    const timer = setTimeout(() => {
      setVerificandoLocalStorage(false)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  // Verificar se está carregando ou verificando localStorage
  if (isLoading || verificandoLocalStorage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        <p className="ml-4 text-gray-600">Verificando autenticação...</p>
      </div>
    )
  }

  // Verificar autenticação (contexto ou localStorage como fallback)
  const hasToken = authUtils.isAuthenticated()
  const hasUser = !!authUtils.getCurrentUser()
  const isAuth = isAuthenticated || (hasToken && hasUser)

  if (!isAuth) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Verificar se precisa ser admin
  if (requireAdmin) {
    // Usar isAdmin do contexto ou verificar role do usuário
    const user = useAuth().user || authUtils.getCurrentUser()
    const userIsAdmin = isAdmin || (user?.role === 'ADMIN')
    if (!userIsAdmin) {
      return <Navigate to="/unauthorized" replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
